import { createWithEqualityFn } from 'zustand/traditional';

export const MAX_PON_TASK_HISTORY = 50;

export type PonTaskStatus = 'scheduled' | 'queued' | 'running' | 'completed' | 'failed' | 'warning' | 'unknown';
export type PonTaskCategory = 'diagnostics' | 'maintenance' | 'inventory' | 'system';
export type PonTaskOrigin = 'user' | 'poller' | 'inventory';

export interface PonTaskEntry {
  id: string;
  deviceId: number;
  taskName: string;
  status: PonTaskStatus;
  message?: string | null;
  priority?: number | null;
  author?: string | null;
  onus?: string[];
  createdAt: string;
  updatedAt?: string;
  source: 'local' | 'ws' | 'inventory';
  origin?: PonTaskOrigin;
  category?: PonTaskCategory;
  scheduledFor?: string | null;
  scope?: 'selection' | 'all';
  metadata?: Record<string, unknown> | null;
}

export interface PonTaskTimelineFilter {
  onlySelection: boolean;
  categories: Record<PonTaskCategory, boolean>;
}

export const DEFAULT_PON_TIMELINE_FILTER: PonTaskTimelineFilter = {
  onlySelection: false,
  categories: {
    diagnostics: true,
    maintenance: true,
    inventory: true,
    system: true,
  },
};

const cloneFilter = (filter: PonTaskTimelineFilter): PonTaskTimelineFilter => ({
  onlySelection: filter.onlySelection,
  categories: { ...filter.categories },
});

interface PonTaskState {
  tasks: PonTaskEntry[];
  filters: Record<number, PonTaskTimelineFilter>;
  add: (entry: PonTaskEntry) => void;
  clear: (deviceId?: number) => void;
  getFilter: (deviceId: number) => PonTaskTimelineFilter;
  setFilter: (deviceId: number, updater: (prev: PonTaskTimelineFilter) => PonTaskTimelineFilter) => void;
}

const resolveCategory = (taskName?: string, fallback?: PonTaskCategory): PonTaskCategory => {
  const name = taskName?.toLowerCase() ?? '';
  if (name.includes('profile') || name.includes('reboot')) {
    return 'maintenance';
  }
  if (name.includes('inventory')) {
    return 'inventory';
  }
  if (name.includes('system')) {
    return 'system';
  }
  return fallback ?? 'diagnostics';
};

const resolveOrigin = (source: PonTaskEntry['source'], explicit?: PonTaskOrigin): PonTaskOrigin => {
  if (explicit) {
    return explicit;
  }
  if (source === 'ws') {
    return 'poller';
  }
  if (source === 'inventory') {
    return 'inventory';
  }
  return 'user';
};

export const useDevicePonTasksStore = createWithEqualityFn<PonTaskState>()((set, get) => ({
  tasks: [],
  filters: {},
  add: (entry) =>
    set((state) => {
      const normalized: PonTaskEntry = {
        ...entry,
        category: resolveCategory(entry.taskName, entry.category),
        origin: resolveOrigin(entry.source, entry.origin),
        createdAt: entry.createdAt ?? new Date().toISOString(),
      };

      const existing = state.tasks.find(
        (task) => task.deviceId === normalized.deviceId && task.id === normalized.id,
      );

      const merged: PonTaskEntry = existing
        ? {
            ...existing,
            ...normalized,
            createdAt: normalized.createdAt ?? existing.createdAt,
            updatedAt:
              normalized.updatedAt ??
              normalized.createdAt ??
              existing.updatedAt ??
              existing.createdAt,
            priority: normalized.priority ?? existing.priority,
            author: normalized.author ?? existing.author,
            message: normalized.message ?? existing.message,
            onus: normalized.onus ?? existing.onus,
            source: normalized.source ?? existing.source,
            origin: normalized.origin ?? existing.origin,
            category: normalized.category ?? existing.category,
            metadata: normalized.metadata ?? existing.metadata,
            scheduledFor: normalized.scheduledFor ?? existing.scheduledFor,
            scope: normalized.scope ?? existing.scope,
          }
        : normalized;

      const withoutDuplicates = state.tasks.filter(
        (task) => !(task.deviceId === merged.deviceId && task.id === merged.id),
      );

      const tasks = [merged, ...withoutDuplicates].slice(0, MAX_PON_TASK_HISTORY);
      return { tasks };
    }),
  clear: (deviceId) =>
    set((state) => {
      if (deviceId == null) {
        return { tasks: [], filters: {} };
      }

      const nextTasks = state.tasks.filter((task) => task.deviceId !== deviceId);
      const nextFilters = { ...state.filters };
      delete nextFilters[deviceId];

      return {
        tasks: nextTasks,
        filters: nextFilters,
      };
    }),
  getFilter: (deviceId) => {
    const current = get().filters[deviceId] ?? DEFAULT_PON_TIMELINE_FILTER;
    return cloneFilter(current);
  },
  setFilter: (deviceId, updater) =>
    set((state) => {
      const current = state.filters[deviceId] ?? DEFAULT_PON_TIMELINE_FILTER;
      const next = updater(cloneFilter(current));
      return {
        filters: {
          ...state.filters,
          [deviceId]: next,
        },
      };
    }),
}));

